import { useQuery } from '@apollo/client';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { GET_JDP_VEHICLE } from '../schemas/FilteredVehiclesSchema';
import { QUERY_VERSION } from '../constants';
import { selectAppConfig } from '../redux/appConfigReducer';
import { FilteredVehicle } from '../types';
import { ELIGIBLE_DEALER_IDS } from '../constants/tier3EligibleDealers';

const useGetByVIn = (vehicle: FilteredVehicle) => {
  const { isTier3page, marketType } = useSelector(selectAppConfig);

  const isUsMarket = marketType === 'USA';
  const isUsedVehicle = vehicle?.vehicleType === 'used';

  const market = useMemo(() => {
    if (!vehicle) return '';
    if (isUsMarket) return isUsedVehicle ? 'usuc' : 'us';

    return isUsedVehicle ? 'cauc' : 'ca';
  }, [isUsMarket, isUsedVehicle, vehicle]);

  const shouldSkipCall = useMemo(() => {
    if (isTier3page) return true;
    if (!vehicle) return false;
    if (ELIGIBLE_DEALER_IDS.includes(vehicle.kvpsSyncId)) return false;
    return true;
  }, [isTier3page, vehicle]);

  const importDb = useMemo(() => {
    if (isUsMarket) return 'AGC_USA_JDP';
    return 'AGC_CAN_JDP';
  }, [isUsMarket]);

  const { data, loading, error } = useQuery(GET_JDP_VEHICLE, {
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
    skip: shouldSkipCall,
    variables: {
      version: QUERY_VERSION,
      market: [market.toUpperCase()],
      lang: 'en',
      filters: `t_vin.${vehicle.vin}`,
      ranges: 'prices.retail,modelYear,powerHP,usedCarMileage',
      sort: 'prices.retail:ASC',
      // we don't need the actual vehicle list in the filter drawer,
      // so we set limit=0 in this case to speed up the process
      limit: 2,
      offset: 0,
      preset: `sold-order.no,stat-import.${importDb}`,
    },
  });

  const foundId = data?.getFilteredVehiclesForWormwood?.vehicles[0]?.id;

  return { foundId, isLoadingUrl: loading };
};

export default useGetByVIn;
